import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Alert, AlertColor, Box, useTheme } from "@mui/material";
import {
  TTM_PAGE_HEADER_HEIGHT_CSS_VAR,
  TTM_TOAST_SNACKBAR_Z_INDEX,
} from "../constants/uiZIndex";

const TOAST_GAP_BELOW_HEADER_PX = 16;
const TOAST_STACK_GAP_PX = 12;
const DEFAULT_SNACKBAR_TOP_PX = 24;

function readPageHeaderHeightPx(): number {
  if (typeof document === "undefined") {
    return 0;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(TTM_PAGE_HEADER_HEIGHT_CSS_VAR)
    .trim();
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function toastStackTopPx(): number {
  const headerPx = readPageHeaderHeightPx();
  return headerPx > 0
    ? headerPx + TOAST_GAP_BELOW_HEADER_PX
    : DEFAULT_SNACKBAR_TOP_PX;
}

interface ToastMessage {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface ToastContextType {
  showToast: (
    message: string,
    severity?: AlertColor,
    duration?: number
  ) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const theme = useTheme();
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [stackTopPx, setStackTopPx] = useState(toastStackTopPx);
  const snackbarZIndex = theme.zIndex.snackbar ?? TTM_TOAST_SNACKBAR_Z_INDEX;

  useEffect(() => {
    const syncTop = () => setStackTopPx(toastStackTopPx());
    syncTop();
    window.addEventListener("resize", syncTop);
    const observer = new MutationObserver(syncTop);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => {
      window.removeEventListener("resize", syncTop);
      observer.disconnect();
    };
  }, []);

  const showToast = (
    message: string,
    severity: AlertColor = "info",
    duration: number = 4000
  ) => {
    const id = Date.now().toString();
    const newToast: ToastMessage = { id, message, severity, duration };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);
  };

  const showSuccess = (message: string, duration?: number) => {
    showToast(message, "success", duration);
  };

  const showError = (message: string, duration?: number) => {
    showToast(message, "error", duration);
  };

  const showWarning = (message: string, duration?: number) => {
    showToast(message, "warning", duration);
  };

  const showInfo = (message: string, duration?: number) => {
    showToast(message, "info", duration);
  };

  const handleClose = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showError, showWarning, showInfo }}
    >
      {children}
      {toasts.length > 0 && (
        <Box
          role="region"
          aria-live="polite"
          aria-label="通知"
          sx={{
            position: "fixed",
            top: `${stackTopPx}px`,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: snackbarZIndex,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: `${TOAST_STACK_GAP_PX}px`,
            pointerEvents: "none",
            maxWidth: "min(480px, 90vw)",
          }}
        >
          {toasts.map((toast) => (
            <Alert
              key={toast.id}
              onClose={() => handleClose(toast.id)}
              severity={toast.severity}
              variant="filled"
              sx={{
                width: "100%",
                minWidth: "300px",
                pointerEvents: "auto",
                boxShadow: 3,
              }}
            >
              {toast.message}
            </Alert>
          ))}
        </Box>
      )}
    </ToastContext.Provider>
  );
};
